import React, { useState } from 'react';
import { Save } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { calculateGrade, generateId } from '../../utils/helpers';

function MarksEntry() {
  const { exams, marks, setMarks, students, classes, subjects, staff } = useApp();
  const { currentUser } = useAuth();
  const isTeacher = currentUser?.role === 'teacher';
  const teacherProfile = isTeacher ? staff.find(s => s.id === currentUser?.profileId) : null;
  const availableClasses = isTeacher
    ? classes.filter(c => (teacherProfile?.classIds || []).includes(c.id))
    : classes;
  const availableSubjects = isTeacher
    ? subjects.filter(s => (teacherProfile?.subjectIds || []).includes(s.id))
    : subjects;

  const [selectedExam, setSelectedExam] = useState(exams[0]?.id || '');
  const [selectedClass, setSelectedClass] = useState(availableClasses[0]?.id || '');
  const [selectedSubject, setSelectedSubject] = useState(availableSubjects[0]?.id || '');
  const [localMarks, setLocalMarks] = useState({});
  const [saved, setSaved] = useState(false);

  const classStudents = students.filter(s => s.classId === selectedClass && s.status === 'active');
  const subject = subjects.find(s => s.id === selectedSubject);

  React.useEffect(() => {
    if (!selectedExam || !selectedSubject) return;
    const currentSubject = subjects.find(s => s.id === selectedSubject);
    const existing = marks.filter(m => m.examId === selectedExam && m.subjectId === selectedSubject);
    const map = {};
    existing.forEach(m => {
      const grade = calculateGrade(m.marksObtained, currentSubject?.maxMarks || 100);
      map[m.studentId] = { marks: m.marksObtained, remarks: grade.comment };
    });
    setLocalMarks(map);
  }, [selectedExam, selectedSubject, selectedClass]);

  const updateMark = (studentId, field, value) => {
    if (field === 'marks') {
      const maxMarks = subject?.maxMarks || 100;
      const comment = value !== '' ? calculateGrade(Number(value), maxMarks).comment : '';
      setLocalMarks(prev => ({
        ...prev,
        [studentId]: { ...(prev[studentId] || {}), marks: value, remarks: comment },
      }));
    } else {
      setLocalMarks(prev => ({
        ...prev,
        [studentId]: { ...(prev[studentId] || {}), [field]: value },
      }));
    }
  };

  const save = () => {
    if (!selectedExam || !selectedSubject) return;
    const filtered = marks.filter(m => !(m.examId === selectedExam && m.subjectId === selectedSubject && classStudents.some(s => s.id === m.studentId)));
    const newMarks = classStudents.map(s => ({
      id: generateId('MRK'), examId: selectedExam, studentId: s.id, subjectId: selectedSubject,
      marksObtained: Number(localMarks[s.id]?.marks || 0), maxMarks: subject?.maxMarks || 100,
      remarks: localMarks[s.id]?.remarks || '', enteredBy: currentUser?.profileId || 'STF-001'
    }));
    setMarks([...filtered, ...newMarks]);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold">Marks Entry</h1></div>
        <button onClick={save} disabled={!selectedExam||!selectedSubject}
          className={`flex items-center gap-2 px-4 py-1 rounded-lg text-sm font-medium disabled:opacity-40 transition-colors ${saved?'bg-green-600 text-white':'bg-indigo-600 hover:bg-indigo-700 text-white'}`}>
          <Save size={16}/>{saved?'Saved!':'Save Marks'}
        </button>
      </div>
      <div className="flex gap-4 mb-6 flex-wrap">
        {[['Exam',selectedExam,setSelectedExam,exams,'name'],['Class',selectedClass,setSelectedClass,availableClasses,null],['Subject',selectedSubject,setSelectedSubject,availableSubjects,'name']].map(([label,val,setter,list,nameKey])=>(
          <div key={label}>
            <label className="block font-medium mb-1">{label}</label>
            <select value={val} onChange={e=>setter(e.target.value)} className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
              {label!=='Exam'&&label!=='Subject'&&<option value="">Select {label}</option>}
              {list.map(item=>(
                <option key={item.id} value={item.id}>
                  {label==='Class'?`${item.name} - Sec ${item.section}`:item[nameKey||'name']}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
      {selectedExam && selectedSubject && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-1 text-left font-semibold">#</th>
                <th className="px-4 py-1 text-left font-semibold">Student</th>
                <th className="px-4 py-1 text-center font-semibold">Marks (/{subject?.maxMarks||100})</th>
                <th className="px-4 py-1 text-center font-semibold">Grade</th>
                <th className="px-4 py-1 text-left font-semibold">Remarks</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {classStudents.map((student,i)=>{
                const marksVal = localMarks[student.id]?.marks || '';
                const grade = marksVal !== '' ? calculateGrade(Number(marksVal), subject?.maxMarks||100) : null;
                return (
                  <tr key={student.id} className="hover:bg-gray-50">
                    <td className="px-4 py-1">{i+1}</td>
                    <td className="px-4 py-1 font-medium">{student.firstName} {student.lastName}</td>
                    <td className="px-4 py-1 text-center">
                      <input type="number" min="0" max={subject?.maxMarks||100} value={marksVal}
                        onChange={e=>updateMark(student.id,'marks',e.target.value)}
                        className="w-20 text-center px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
                    </td>
                    <td className="px-4 py-1 text-center">
                      {grade && <span className={`font-bold ${grade.color}`}>{grade.grade}</span>}
                    </td>
                    <td className="px-4 py-1">
                      <input type="text" value={localMarks[student.id]?.remarks||''}
                        onChange={e=>updateMark(student.id,'remarks',e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" placeholder="Remarks..." />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
export default MarksEntry;
